import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useRef, useLayoutEffect, } from 'react';
import { AutoGrid, TrayLayout, styled, useTheme } from '@streem/ui-react';
import { isOnSiteRoomSession } from '@streem/sdk-core';
import { CenterToolBar, RightToolBar } from '../tool_bar';
import { useObservable, useVideoEnabled } from '../../..';
import { useRoomSession } from '../../../context/room_session';
import { useGuideUI } from '../../../hooks/use_guide_ui';
import { useBookmarkToSelect } from '../../../hooks/use_bookmark_to_select';
import { hasWorldSubject, useCameraSubject } from '../../../hooks/use_camera_subject';
import { SuccessfulStreemshot } from '../successful_streemshot';
import { VideoFrame } from '../video_frame/video_frame';
import { scan } from 'rxjs/operators';
import { growElementToFillParentContainer, moveElementToCoverTargetElement, shrinkElementToBottomRightCornerOfParentContainer, flipAndBlurKeyframes, } from '../../../animations';
import { CheckClientSupport } from './two_way_video/check_client_support';
import { useInCallShortcuts } from '../../../hooks/use_in_call_shortcuts';
import { InCallDetailsContentTray } from './in_call_details_content_tray';
import { RecordingIndicator } from '../video_frame/recording/recording_indicator';
export function SpectatorCallFrame() {
    const ANIMATION_DELAY = 200;
    const ANIMATION_DURATION = 500;
    const preventAnimations = useRef(true);
    const [animating, setAnimating] = useState(false);
    const [faceToWorld, setFaceToWorld] = useState(false);
    const theme = useTheme();
    const roomSession = useRoomSession();
    const [trayOpen, setTrayOpen] = useState(true);
    const { guideUI, setGuideUI } = useGuideUI();
    const [bookmarkToSelect] = useBookmarkToSelect();
    const [videoControllers] = useObservable(roomSession.media.videoControllers);
    const hasWorld = hasWorldSubject(roomSession.media.videoControllers);
    const fullScreen = hasWorld;
    const [showArrow, setShowArrow] = useState(false);
    const [flipArrow, setFlipArrow] = useState(false);
    const [remoteUser] = useObservable(roomSession.user.remoteUser);
    const [localVideoEnabled] = useVideoEnabled(roomSession.media.videoControllers.pipe(scan((acc, controllers) => {
        return controllers.find(controller => controller.ownerIsLocalUser) || acc;
    }, undefined)));
    const cameraSubjects = useCameraSubject(roomSession.media.videoControllers);
    const [remoteVideoEnabled] = useVideoEnabled(roomSession.media.videoControllers.pipe(scan((acc, controllers) => {
        return controllers.find(controller => !controller.ownerIsLocalUser) || acc;
    }, undefined)));
    const [remoteIsARCapable] = useObservable(roomSession.ar.remoteParticipantIsARCapable);
    const remoteShadowRef = useRef(null);
    const remoteRef = useRef(null);
    const localShadowRef = useRef(null);
    const localRef = useRef(null);
    useInCallShortcuts();
    useEffect(() => {
        if (bookmarkToSelect) {
            setTrayOpen(true);
        }
    }, [bookmarkToSelect]);
    const videoFrames = useMemo(() => {
        if (!videoControllers || !videoControllers.length) {
            return undefined;
        }
        return videoControllers
            .sort(controller => {
            return controller.ownerIsLocalUser ? 1 : -1;
        })
            .map(controller => {
            if (fullScreen) {
                setShowArrow(false);
            }
            const localPipView = faceToWorld && controller.ownerIsLocalUser;
            const remoteFullscreenView = faceToWorld && !controller.ownerIsLocalUser;
            const styles = {};
            /*
            To persist component styles after animations end,
            we update their style objects to match animation end keyframe styles
        */
            if (localPipView) {
                styles.width = '267px';
                styles.height = '150px';
                styles.bottom = '20px';
                styles.right = '20px';
                styles.position = 'absolute';
                styles.backgroundColor = theme.colors.grey90;
                // fade pip in/out on video feed toggle
                styles.transition = `opacity ${ANIMATION_DURATION}ms linear`;
                styles.opacity = localVideoEnabled ? '1' : '0';
                styles.zIndex = 3;
            }
            else if (remoteFullscreenView) {
                styles.position = 'absolute';
                styles.top = '0px';
                styles.left = '0px';
                styles.height = 'var(--auto-grid-height, 100%)';
                styles.width = '100%';
                styles.backgroundColor = theme.colors.black;
                styles.transition = `border-radius 200ms ease-in`;
                styles.borderRadius = '0px';
            }
            // Style correctly for 1WV view mode
            if (videoControllers.length === 1) {
                styles.backgroundColor = theme.colors.black;
            }
            // Don't allow drawing on your own camera, unless in onsite session
            if (!isOnSiteRoomSession(roomSession) && controller.ownerIsLocalUser) {
                styles.pointerEvents = 'none';
            }
            return (_jsx(VideoFrame, { ref: controller.ownerIsLocalUser ? localRef : remoteRef, videoController: controller, showArrow: showArrow, flipArrow: flipArrow, animationOptions: {
                    animating: controller.ownerIsLocalUser ? false : animating,
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                }, cardOptions: {
                    constrainContainerToCanvas: !controller.ownerIsLocalUser && faceToWorld,
                    styles: styles,
                    localPipView,
                } }, controller.id));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        videoControllers,
        fullScreen,
        showArrow,
        localRef,
        remoteRef,
        faceToWorld,
        localVideoEnabled,
        ANIMATION_DURATION,
        animating,
        trayOpen,
    ]);
    useLayoutEffect(() => {
        // This animation should only run when switching from FACE_TO_FACE to FACE_TO_WORLD or vice versa.  Skip running when the
        //      call initially starts.  When the calls starts, this component renders initially with an empty cameraSubjects array
        //      and then renders again with it populated.  Wait until cameraSubjects is populated before unsetting preventAnimations.
        if (preventAnimations.current && cameraSubjects.length > 1) {
            preventAnimations.current = false;
            return;
        }
        if (cameraSubjects.length > 1 &&
            remoteRef &&
            localRef &&
            remoteRef.current &&
            localRef.current &&
            localShadowRef &&
            localShadowRef.current &&
            remoteShadowRef &&
            remoteShadowRef.current &&
            !preventAnimations.current) {
            // transition from FACE_TO_FACE to FACE_TO_WORLD
            if (fullScreen) {
                setAnimating(true);
                const animationOptions = {
                    easing: 'ease-out',
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                };
                const growRemoteElementToFillParentContainerKeyFrames = growElementToFillParentContainer(remoteRef.current, remoteRef.current.offsetParent);
                const shrinkLocalElementToPipLocation = shrinkElementToBottomRightCornerOfParentContainer(localRef.current, localRef.current.offsetParent, { right: 20, bottom: 20, width: 267, height: 150 });
                let canvasWrapperElement = null;
                // Do not animate remote canvas during tranistion if video feed is disabled
                if (remoteRef.current.querySelector('[data-queryselector="canvas-wrapper"]') &&
                    remoteVideoEnabled) {
                    canvasWrapperElement = remoteRef.current.querySelector('[data-queryselector=canvas-wrapper]');
                }
                if (canvasWrapperElement) {
                    canvasWrapperElement.animate(flipAndBlurKeyframes, animationOptions);
                }
                remoteRef.current.animate(growRemoteElementToFillParentContainerKeyFrames, animationOptions);
                const localAnimation = localRef.current.animate(shrinkLocalElementToPipLocation, animationOptions);
                localAnimation.onfinish = () => {
                    setFaceToWorld(true);
                    setAnimating(false);
                };
                // transition from FACE_TO_WORLD to FACE_TO_FACE
            }
            else if (!fullScreen) {
                setAnimating(true);
                const animationOptions = {
                    easing: 'ease-out',
                    duration: ANIMATION_DURATION,
                    delay: ANIMATION_DELAY,
                };
                const moveRemoteElementToCoverShadowRemoteElement = moveElementToCoverTargetElement(remoteRef.current, remoteShadowRef.current, remoteShadowRef.current.offsetParent);
                const moveLocalElementToCoverShadowLocalElement = moveElementToCoverTargetElement(localRef.current, localShadowRef.current, localShadowRef.current.offsetParent);
                let canvasWrapperElement = null;
                if (remoteRef.current.querySelector('[data-queryselector=canvas-wrapper]') &&
                    remoteVideoEnabled) {
                    canvasWrapperElement = remoteRef.current.querySelector('[data-querySelector=canvas-wrapper]');
                }
                if (canvasWrapperElement) {
                    canvasWrapperElement.animate(flipAndBlurKeyframes, animationOptions);
                }
                remoteRef.current.animate(moveRemoteElementToCoverShadowRemoteElement, animationOptions);
                // Reveal the pro's PiP video (if video OFF) before growing/moving back to grid position
                localRef.current.style.opacity = '1';
                const animation = localRef.current.animate(moveLocalElementToCoverShadowLocalElement, animationOptions);
                animation.onfinish = () => {
                    if (localRef && localRef.current) {
                        localRef.current.style.opacity = '';
                    }
                    setFaceToWorld(false);
                    setAnimating(false);
                };
            }
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
    }, [hasWorld, cameraSubjects.length]);
    return (_jsx(TrayLayout, { isOpen: trayOpen, onToggleClick: () => {
            if (trayOpen) {
                document.activeElement.blur();
            }
            setTrayOpen(!trayOpen);
        }, toggleText: 'Details', stageContent: _jsxs(Container, { children: [_jsx(CheckClientSupport, {}), _jsx(SuccessfulStreemshot, {}), _jsx(RecordingIndicator, { trayOpen: trayOpen }), _jsx(AutoGrid, { aspectRatio: 16 / 9, minimumGap: 10, options: {
                        fillScreenWhenOneChild: videoControllers?.length === 1,
                        setSmallGap: setFlipArrow,
                    }, children: videoFrames?.map(videoFrame => {
                        if (!videoFrame) {
                            return null;
                        }
                        const isLocalUser = videoFrame?.props.videoController.ownerIsLocalUser;
                        return (_jsx(ShadowDiv, { ref: isLocalUser ? localShadowRef : remoteShadowRef, children: videoFrame }, videoFrame.props.videoController.id));
                    }) })] }), toolbarCentralContent: _jsx(CenterToolBar, { isTrayOpen: trayOpen, setShowArrow: setShowArrow, remoteUserName: remoteUser?.name }), toolbarRightContent: _jsx(RightToolBar, { isTrayOpen: trayOpen, handleGuideToggle: () => setGuideUI(!guideUI), showGuideUI: guideUI, remoteIsARCapable: remoteIsARCapable }), trayContent: _jsx(InCallDetailsContentTray, { isTrayOpen: trayOpen }) }));
}
const Container = styled.div({
    height: '100%',
    width: '100%',
    backgroundColor: 'black',
    display: 'flex',
    flexDirection: 'column',
});
const ShadowDiv = styled.div `
    height: var(--item-height, 100%);
    width: var(--item-width, 100%);
`;
//# sourceMappingURL=spectator_call_frame.js.map